import React, { useRef } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import WordpressEmbedLayout from '../../layouts/WordpressEmbedLayout';
import MainFilterBar from '../../components/MainFilterBar';
import SecondaryFilterBar from '../../components/SecondaryFilterBar';
import NoSearchResults from '../../components/NoSearchResults';
import SearchResults from '../../components/SearchResults';

const PAGE_SIZE = 10;
const GET_FACILITIES = gql`
  query GetFacilities($condition: facilities_bool_exp!, $offset: Int!, $limit: Int!) {
    facilities(
      order_by: [{name: asc}],
      offset: $offset,
      limit: $limit,
      where: $condition
    ) {
      id
      name
      capacity
      address
      city
      state
      zipcode
      phone_number
      admin_name
      email
      monthly_price_start
      monthly_price_end
      hospice
      dementia
      pets_allowed
      care_charges
      ccl_facility_id
      ccl_link
      accommodations {
        id
        is_private
        monthly_price_start
        monthly_price_end
        all_inclusive_price
        notes
        price_notes
      }
    }
    facilities_aggregate(where: $condition) {
      aggregate {
        count
      }
    }
  }
`;

const searchParamsToObject = (searchParams) => {
  const state = {};
  for (const [key, value] of searchParams.entries()) {
    state[key] = value;
  }
  return state;
};

const filtersToQueryCondition = (filters) => {
  const [costStart, costEnd] = filters.cost && filters.cost !== 'any' ? filters.cost.trim().split(/\s*-\s*/) : [];
  const [capacityStart, capacityEnd] = filters.capacity && filters.capacity !== 'any' ? filters.capacity.trim().split(/\s*-\s*/) : [];
  return {
    _and: [
      ...(filters.zipcode && filters.zipcode.trim().length > 0 ? [{ zipcode: { _in: filters.zipcode.trim().split(/\s*,\s*/) } }] : []),
      ...(filters.accommodation_type && filters.accommodation_type !== 'any' ? (
        filters.accommodation_type === 'semi-private' ? [{ accommodations: { is_private: { _eq: false } } }] : (
          filters.accommodation_type === 'private' ? [{ accommodations: { is_private: { _eq: true } } }] :
          []
        )
      ) : []),
      ...(costStart ? [{
        _or: [
          { monthly_price_end: { _is_null: true } },
          { monthly_price_end: { _gte: costStart } }
        ]
      }] : []),
      ...(costEnd ? [{
        _or: [
          { monthly_price_start: { _is_null: true } },
          { monthly_price_start: { _lte: costEnd } }
        ]
      }] : []),
      ...(capacityStart ? [{ capacity: { _gte: capacityStart } }] : []),
      ...(capacityEnd ? [{ capacity: { _lte: capacityEnd } }] : []),
      ...(filters.dementia && filters.dementia !== 'any' ? [{ dementia: { _eq: filters.dementia } }] : [])
    ]
  };
};

const Search = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const ref = useRef(null);

  const { loading, error, data } = useQuery(GET_FACILITIES, {
    variables: {
      condition: filtersToQueryCondition(searchParamsToObject(searchParams)),
      offset: searchParams.has('offset') ? parseInt(searchParams.get('offset')) : 0,
      limit: searchParams.has('limit') ? parseInt(searchParams.get('limit')) : PAGE_SIZE
    }
  });

  if (! loading && error) {
    console.error(error);
  }

  const setSearchParamsPartial = (partialUpdate) => {
    setSearchParams({
      ...searchParamsToObject(searchParams),
      ...partialUpdate
    });
  };
  const setFiltersPartial = (partialUpdate) => {
    // when filters change, also set the page offset to 0
    partialUpdate.offset = 0;
    setSearchParamsPartial(partialUpdate);
  };

  return (
    <WordpressEmbedLayout anchor={ref}>
      <MainFilterBar searchParams={searchParams} setFilters={setFiltersPartial} />
      <SecondaryFilterBar searchParams={searchParams} setFilters={setFiltersPartial} />
      {! loading && (data.facilities_aggregate.aggregate.count < 1) &&
        <NoSearchResults />
      }
      {! loading && (data.facilities_aggregate.aggregate.count > 0) &&
        <SearchResults
          facilities={data.facilities}
          totalResults={data.facilities_aggregate.aggregate.count}
          searchParams={searchParams}
          defaultLimit={PAGE_SIZE}
          setPagination={setSearchParamsPartial}
          anchor={ref}
        />
      }
    </WordpressEmbedLayout>
  );
};

export default Search;
